import { useTitleStore } from 'src/stores/useTitleStore';
import * as React from 'react';
import { useIntl } from 'react-intl';
import { billingMessages } from 'src/features/billing/utils/messages';
import { Button } from 'src/registry/ui/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ICON_MAP } from 'src/utils/constants';
import { Link, useParams } from 'react-router-dom';
import { useGetBillingEntity } from 'src/features/billing/api/useGetBillingEntity';
import { SectionBlock, SectionBlockHeader, SectionBlockTitle } from 'src/registry/components/blocks/SectionBlock';
import { commonMessages, generalMessages } from 'src/utils/messages';
import DashboardGeneralBlock from 'src/features/billing/components/DashboardGeneralBlock';
import UpdateBillingEntityFormModal from 'src/features/billing/components/forms/UpdateBillingEntityFormModal';
import BillingEntityDashboardTables from 'src/features/billing/components/BillingEntityDashboardTables';
import DeleteBillingEntityFormModal from 'src/features/billing/components/forms/DeleteBillingEntityFormModal';
import { FINANCIAL_DOC_TEMPLATE_TRANSLATION_MAP } from 'src/features/billing/utils/constants';
import useHasPermissions from 'src/utils/useHasPermissions';
import { PERMISSION } from 'src/features/auth/types';

const BillingEntityDashboard = () => {
    const { formatMessage } = useIntl();
    const { entityId = '' } = useParams();

    const permissionManageBillingEntities = useHasPermissions([PERMISSION.MANAGE_BILLING_ENTITIES]);

    const { setTitle, removeTitle } = useTitleStore();

    React.useEffect(() => {
        setTitle(formatMessage(billingMessages.billingEntityDashboard));
        return removeTitle;
    }, [formatMessage, removeTitle, setTitle]);

    const { data: billingEntityData } = useGetBillingEntity({
        id: entityId,
    });

    const dashboardBlockData = {
        section1: [
            {
                title: billingMessages.legalName,
                value: billingEntityData?.name ?? '-',
            },
            {
                title: billingMessages.legalNumber,
                value: billingEntityData?.uic ?? '-',
            },
            {
                title: billingMessages.taxNumber,
                value: billingEntityData?.tax_registration_number ?? '-',
            },
            {
                title: billingMessages.legalRepresentative,
                value: billingEntityData?.representative ?? '-',
            },
            {
                title: commonMessages.email,
                value: billingEntityData?.email && billingEntityData.email !== '' ? billingEntityData.email : '-',
            },
            {
                title: commonMessages.phone,
                value: billingEntityData?.phone ?? '-',
            },
        ],
        section2: [
            {
                title: commonMessages.address,
                value: billingEntityData?.address ?? '-',
            },
            {
                title: commonMessages.city,
                value: billingEntityData?.city ?? '-',
            },
            {
                title: commonMessages.country,
                value: billingEntityData?.country ?? '-',
            },
        ],
        section3: [
            {
                title: billingMessages.tenantId,
                value: billingEntityData?.tenant?.id ?? '-',
            },
        ],
        section4: [
            {
                title: billingMessages.invoicePrefix,
                value: billingEntityData?.invoice_prefix ?? '-',
            },
            {
                title: billingMessages.invoiceFooter,
                value: billingEntityData?.invoice_footer ?? '-',
            },
        ],
        section5: [
            {
                title: billingMessages.financialDocPrintTemplate,
                value: billingEntityData?.financial_document_print_template
                    ? formatMessage(
                          FINANCIAL_DOC_TEMPLATE_TRANSLATION_MAP[billingEntityData?.financial_document_print_template],
                      )
                    : '-',
            },
            {
                title: billingMessages.financialDocColor,
                value: billingEntityData?.financial_document_print_color ? (
                    <div className="flex flex-row gap-2">
                        <div
                            className="h-5 w-5 border"
                            style={{ backgroundColor: billingEntityData?.financial_document_print_color }}
                        ></div>
                        <div>{billingEntityData?.financial_document_print_color}</div>
                    </div>
                ) : (
                    '-'
                ),
            },
            {
                title: billingMessages.financialDocLogo,
                value: billingEntityData?.financial_document_print_logo ? (
                    <div className="flex h-[125px] w-[174px] items-center justify-center overflow-hidden rounded-[8px]">
                        <img
                            src={billingEntityData?.financial_document_print_logo}
                            className="max-h-full max-w-full object-contain"
                            alt={formatMessage(billingMessages.financialDocLogo)}
                        />
                    </div>
                ) : (
                    '-'
                ),
            },
        ],
    };

    return (
        <>
            <Button asChild className="place-self-start p-0" variant="link">
                <Link to={`/billing-entity`}>
                    <FontAwesomeIcon icon={ICON_MAP.BACK} className="opacity-50" />
                    {formatMessage(billingMessages.backToAllBillingEntities)}
                </Link>
            </Button>

            <div className="mt-6 grid grid-cols-1 gap-8">
                <SectionBlock>
                    <SectionBlockHeader className="flex min-h-9 flex-row justify-between">
                        <SectionBlockTitle className="text-2xl whitespace-nowrap">
                            {billingEntityData?.name}
                        </SectionBlockTitle>

                        {permissionManageBillingEntities && billingEntityData && (
                            <div className="flex flex-row gap-2">
                                <UpdateBillingEntityFormModal
                                    selectedBillingEntity={billingEntityData}
                                    semantics={{
                                        entryName: formatMessage(billingMessages.billingEntity).toLowerCase(),
                                    }}
                                >
                                    <Button size="mdIcon" variant="outline" className="cursor-pointer">
                                        <FontAwesomeIcon icon={ICON_MAP.EDIT} />
                                        <span className="hidden md:block">{formatMessage(commonMessages.edit)}</span>
                                    </Button>
                                </UpdateBillingEntityFormModal>
                                <DeleteBillingEntityFormModal
                                    selectedBillingEntity={billingEntityData}
                                    semantics={{
                                        entryName: formatMessage(billingMessages.billingEntity).toLowerCase(),
                                    }}
                                >
                                    <Button size="mdIcon" variant="destructiveLight" className="cursor-pointer">
                                        <FontAwesomeIcon icon={ICON_MAP.DELETE} />
                                        <span className="hidden md:block">{formatMessage(generalMessages.delete)}</span>
                                    </Button>
                                </DeleteBillingEntityFormModal>
                            </div>
                        )}
                    </SectionBlockHeader>
                    <DashboardGeneralBlock dashboardData={dashboardBlockData} indexForLastSeparator={4} />
                </SectionBlock>

                <BillingEntityDashboardTables />
            </div>
        </>
    );
};

export default BillingEntityDashboard;
